import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLink, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { HoverCallout } from 'components/Utils/HoverCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  globalRichTextProseStyles,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  globalTextStylesOverlineRoot,
} from 'globalStyles';
import ISOControl from 'models/isoControl';
import { AnchorHTMLAttributes, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  getISOControlReferenceUrl,
  loadISOControlReference,
  resolveISOControlReferenceUrl,
} from 'services/Api/isoControlService';
import { getHtmlJSX } from 'utils/html';

interface IISOControlReferenceProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  isoControlId: number;
}

// Hover peeks at the clause; a plain click opens the same card. Modifier clicks keep the link's own behaviour.
const ISOControlReference = ({ isoControlId, children, target, ...attributes }: IISOControlReferenceProps) => {
  const app = useContext(AppContext);
  const { t } = useTranslation(['translation']);
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [clause, setClause] = useState<ISOControl>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // undefined = not resolved yet, null = no accessible requirement or control in this organization
  const [destination, setDestination] = useState<string | null>();
  const href = getISOControlReferenceUrl(app, isoControlId);
  const caption = clause
    ? t('translation:RichText.Reference.Caption', { standard: clause.isoNorm?.name ?? '', code: clause.code })
    : '';

  useEffect(() => {
    if (!open) return;
    let active = true;
    setFailed(false);
    setClause(undefined);
    loadISOControlReference(app, isoControlId)
      .then((value) => {
        if (active) setClause(value);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    if (destination === undefined) {
      resolveISOControlReferenceUrl(app, isoControlId).then((url) => {
        if (active) setDestination(url ?? null);
      });
    }

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isoControlId, attempt]);

  return (
    <HoverCallout
      isOpen={open}
      onOpenChange={setOpen}
      openOnHover={!app.isMobileView}
      openOnClick={false}
      openDelayMs={350}
      closeDelayMs={200}
      calloutLeaveBehavior="delay"
      directionalHint={IPDirectionalHint.bottomLeftEdge}
      gapSpace={6}
      calloutProps={{
        calloutMaxHeight: 320,
        styles: {
          calloutMain: {
            width: 336,
            maxWidth: 'calc(100vw - 32px)',
            padding: `${globalStackTokensGapSmall.childrenGap}px 14px`,
          },
        },
      }}
      content={
        <IPStack tokens={globalStackTokensGapSmall}>
          <IPText variant="small" styles={{ root: globalTextStylesOverlineRoot }}>
            {caption}
          </IPText>
          {!clause && !failed && <IPSpinner />}
          {failed && (
            <>
              <IPText>{t('translation:RichText.Reference.Unavailable')}</IPText>
              <IPDefaultButton
                text={t('translation:RichText.Reference.Retry')}
                onClick={() => setAttempt(attempt + 1)}
              />
            </>
          )}
          {clause && (
            <>
              <IPText variant="mediumPlus" styles={globalTextStylesBold} block>
                {clause.name}
              </IPText>
              <IPStack.Item styles={{ root: globalRichTextProseStyles }}>{getHtmlJSX(clause.description)}</IPStack.Item>
              <IPSeparator />
              {destination && (
                <IPLink
                  onClick={() => {
                    setOpen(false);
                    history.push(destination);
                  }}
                >
                  {t('translation:RichText.Reference.OpenCode', { code: clause.code })}
                </IPLink>
              )}
              {destination === null && <IPText>{t('translation:RichText.Reference.DestinationUnavailable')}</IPText>}
            </>
          )}
        </IPStack>
      }
    >
      <IPLink
        {...attributes}
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : attributes.rel}
        styles={{
          root: {
            textDecoration: 'underline',
            textDecorationStyle: open ? 'solid' : 'dotted',
            textUnderlineOffset: 3,
            selectors: {
              ':hover': { textDecorationStyle: 'solid' },
            },
          },
        }}
        onClick={(event) => {
          // Stopped here so the read-only host does not switch into edit mode on the same click.
          event.stopPropagation();
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || target === '_blank') return;
          event.preventDefault();
          setOpen(true);
        }}
        onKeyDown={(event) => {
          // Enter activates the link; the read-only host must not also switch into edit mode.
          if (event.key === 'Enter') event.stopPropagation();
        }}
      >
        {children}
      </IPLink>
    </HoverCallout>
  );
};

export default ISOControlReference;
